import React, { useEffect, useRef } from 'react';
import { FeedbackCategory } from 'nekst-api';
import {
  FormTemplate,
  TextArea,
  SubmitButton,
  CancelButton,
} from 'ui-builder';
import { useChatWidgetContext } from '../../ChatWidgetContext';
import InChatFormTheme from './InChatFormTheme';
import FeedbackCategoryDropdown from './FeedbackCategoryDropdown';
import styles from '../ChatWidget.module.scss';

interface FeedbackFormData {
  description: string;
  category: FeedbackCategory | '';
}

export function FeedbackSuccessMessage(props: { onDismiss: () => void }) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onDismissRef = useRef(props.onDismiss);
  onDismissRef.current = props.onDismiss;

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      onDismissRef.current();
    }, 5000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <button
      type="button"
      className={styles.reportIssueSuccess}
      onClick={props.onDismiss}
      data-testid="chat-report-issue-success"
    >
      Thanks for helping us improve Nekst AI!
    </button>
  );
}

export function ReportIssueForm(props: {
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const { submitFeedback } = useChatWidgetContext();

  return (
    <InChatFormTheme>
      <FormTemplate<FeedbackFormData>
        submitFormFunc={async (data) => {
          await submitFeedback(
            data.description,
            data.category || null,
          );
          return data;
        }}
        afterSubmitFunc={async () => {
          props.onSuccess();
        }}
        initialData={{ description: '', category: '' }}
        validationSchema={{
          description: {
            type: 'string',
            constraints: { required: true },
          },
        }}
        buttons={(
          <div className={styles.reportIssueActions}>
            <FeedbackCategoryDropdown />
            <CancelButton
              text="Cancel"
              onClick={async () => { props.onCancel(); }}
            />
            <SubmitButton text="Submit" />
          </div>
        )}
        className={styles.reportIssueFormBody}
        testID="chat-report-issue-form-template"
      >
        <TextArea
          source="description"
          label="Description"
          hideLabel
          placeholder="Describe what went wrong..."
        />
      </FormTemplate>
    </InChatFormTheme>
  );
}
